import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import type {NotificationButtonProps} from './notification-button.types';
import {NOTIFICATION_POLLING_INTERVAL} from './notification-button.types';
import styles from './notification-button.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize} from '@Components/icon-v2/icon.types';
import {useFetchLazyLoadedNotifications, useNewNotifications, useNewNotificationsCount} from '@Panels/notifications-panel/notifications-panel-hooks';
import {fetchNewNotifications} from '@Libraries/notifications-panel-library';
import {ClickableDiv} from '@Components/clickable-div';
import {noop} from '@Utils/general.util';
import {Text, TextSize} from '@Components/text';

export function NotificationButton({...props}: NotificationButtonProps): ReactElement {
  const newNotifications = useNewNotifications();
  const hasNewNotifications = newNotifications.length > 0;
  const fetchNotifications = useFetchLazyLoadedNotifications();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const newNotificationsCount = useNewNotificationsCount();
  const notificationsExceedingLimit = newNotificationsCount > 99;


  const getNewNotificationsCount = (): string => {
    if (notificationsExceedingLimit) {
      return '99+';
    }

    return String(newNotificationsCount);
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    if (document.visibilityState === 'visible') {
      startPollingForNewNotifications();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return (): void => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopPollingForNewNotifications();
    };
  }, []);

  const startPollingForNewNotifications = (): void => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      fetchNewNotifications();
    }, NOTIFICATION_POLLING_INTERVAL);
  };

  const stopPollingForNewNotifications = (): void => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      startPollingForNewNotifications();
    } else {
      stopPollingForNewNotifications();
    }
  };

  return (
    <ClickableDiv
      onClick={props.onClick ?? noop}
      className={`${notificationsExceedingLimit ? styles.marginRightLargeCount : styles.marginRight} ${styles.mobileMargin} ${props.className} _unpadded _position-relative`}
    >
      {hasNewNotifications ? (
        <>
          <Icon type={props.buttonType} shape={props.buttonShape ?? IconShape.SQUARE} size={IconSize.SIZE_ICON_20} icon={'icon-notification-new-empty'} isActive={props.isActive} />
          <div
            className={`flex-center ${styles.pillStyles} ${notificationsExceedingLimit ? styles.pillLargeCount : ''} bg-danger-100 radius-round _pointer-events-none _position-absolute`}
          >
            <Text val={getNewNotificationsCount()} size={TextSize.XXXSMALL} bold className={`content-body-white ${styles.pillTextStyles}`} />
          </div>
        </>
      ) : (
        <Icon shape={props.buttonShape ?? IconShape.SQUARE} size={IconSize.SIZE_ICON_20} type={props.buttonType} icon={'icon-notification'} isActive={props.isActive}></Icon>
      )}
    </ClickableDiv>
  );
}
